import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";
import { Container, Sprite } from "pixi.js";

import { EventsManager } from "../../../../abstracts/events/EventsManager";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { ScreenProperties } from "../../../../abstracts/types/interfaces";
import { isAllTrue } from "../../../../abstracts/utils/ArrayUtils";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { getRandomBetween, getScreenProperties } from "../../../../abstracts/utils/JSUtils";
import { playSound, playSoundOnce, stopSound } from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import {
    ApocalypseSoundNames,
    ApocalypseSpineNames,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { reelsConfig, symbolConfig, symbolLockDelay } from "../../../config/ApocalypseConfig";
import { HIDE_FS_ELEMENTS, RESET_FAKE_SCATTERS } from "../../../events/ApocalypseEvents";
import {
    ApocalypseHighSymbolNames,
    ApocalypseSpecialSymbolNames,
    ApocalypseSymbolNames,
} from "../../../types/ApocalypseEnums";
import { shake } from "../../../utils/ApocalypseGraphicUtils";
import { ApocalypseNormalContainer } from "../normal/ApocalypseNormalContainer";
import { ApocalypseBlankSymbol } from "../symbols/ApocalypseBlankSymbol";
import { ApocalypseScatterSymbol } from "../symbols/ApocalypseScatterSymbol";
import { ApocalypseSymbol } from "../symbols/ApocalypseSymbol";
import { getSymbolFromPool } from "../symbols/ApocalypseSymbolsPool";
import { ApocalypseFsLeftContainer } from "./ApocalypseFsLeftContainer";
import { ApocalypseFsMultiplierContainer } from "./ApocalypseFsMultiplierContainer";
import { ApocalypseFsReel } from "./ApocalypseFsReel";
import { FsHighLocker } from "./FsHighLocker";
import { FsLowLocker } from "./FsLowLocker";

export class ApocalypseFsContainer extends ApocalypseNormalContainer {
    private overground: Sprite;
    private middleBlockersContainer: Container;
    private topBottomBlockersContainer: Container;

    public fsLeftContainer: ApocalypseFsLeftContainer;
    public fsMultiplierContainer: ApocalypseFsMultiplierContainer;
    public topBottomBlockers: AbstractSpine[];
    public alreadyUnlocked: boolean = false; // ! don't forget to reset

    public persistentLockers: FsHighLocker[];

    public lowLockerSoundNames: string[] = [
        ApocalypseSoundNames.LOW_LOCKER_1,
        ApocalypseSoundNames.LOW_LOCKER_2,
        ApocalypseSoundNames.LOW_LOCKER_3,
        ApocalypseSoundNames.LOW_LOCKER_4,
        ApocalypseSoundNames.LOW_LOCKER_5,
        ApocalypseSoundNames.LOW_LOCKER_6,
    ];
    public lowLockerCurrentSoundIndex: number = 0;

    constructor(name: string = `ApocalypseFsContainer`) {
        super(name);

        this.onOrientationChange();
    }

    protected override init() {
        this.fsLeftContainer = new ApocalypseFsLeftContainer();
        this.fsLeftContainer.x = -22;
        this.addChild(this.fsLeftContainer);

        super.init();

        this.fsMultiplierContainer = new ApocalypseFsMultiplierContainer();
        this.addChild(this.fsMultiplierContainer);
    }

    protected override initEvents() {
        EventsManager.on(HIDE_FS_ELEMENTS, () => {
            gsap.to(this.fsLeftContainer, { alpha: 0, duration: 0.2 });
            gsap.to(this.fsMultiplierContainer, { alpha: 0, duration: 0.2 });
        });
    }

    protected addBgFrame() {}

    protected initExtra() {
        this.topBottomBlockers = [];

        this.topBottomBlockersContainer = new Container();
        this.topBottomBlockersContainer.name = `topBottomBlockersContainer`;
        this.topBottomBlockersContainer.x = 52;
        this.topBottomBlockersContainer.y = -4;
        this.addChild(this.topBottomBlockersContainer);

        for (let i = 0; i < reelsConfig.reelsNumber; i++) {
            //top blockers
            const topBlocker = new AbstractSpine(ApocalypseSpineNames.REEL_BLOCKER_TOP);
            topBlocker.setSkinByName(`locked_${getRandomBetween(1, 3, true)}`);
            topBlocker.x = i * 107;
            topBlocker.name = `topBlocker_` + i;
            this.topBottomBlockersContainer.addChild(topBlocker);
            this.topBottomBlockers.push(topBlocker);
        }

        for (let i = 0; i < reelsConfig.reelsNumber; i++) {
            //bottom blockers
            const bottomBlocker = new AbstractSpine(ApocalypseSpineNames.REEL_BLOCKER_TOP);
            bottomBlocker.setSkinByName(`locked_${getRandomBetween(1, 3, true)}`);
            bottomBlocker.x = i * 107;
            bottomBlocker.y = 435;
            bottomBlocker.name = `bottomBlocker_` + i;
            bottomBlocker.scale.y = -bottomBlocker.scale.y;
            this.topBottomBlockersContainer.addChild(bottomBlocker);
            this.topBottomBlockers.push(bottomBlocker);
        }

        this.middleBlockersContainer = new Container();
        this.middleBlockersContainer.name = `middleBlockersContainer`;
        this.addChild(this.middleBlockersContainer);

        this.persistentLockers = [];

        this.overground = generateScaledSprite(`apocalypse/reel_distinction_FS`);
        this.overground.name = `fsOverground`;
        this.overground.x = -10;
        this.overground.y = -116;
        this.overground.alpha = 0;
    }

    public addFakeScatter(reelIndex: number): void {
        const scatterSymbol = getSymbolFromPool(
            ApocalypseSymbolNames.SCATTER
        ) as ApocalypseScatterSymbol;

        let globalPosition = this.reels[reelIndex].getGlobalPosition();
        let localPosition = this.toLocal(globalPosition);

        scatterSymbol.x = localPosition.x;
        scatterSymbol.y = localPosition.y;

        this.addChild(scatterSymbol);

        if (!this.fakeScatters) {
            this.fakeScatters = new Array(this.reels.length).fill(null);
        }

        this.fakeScatters[reelIndex] = scatterSymbol;

        this.fsLeftContainer.alpha = 1;
        this.fsMultiplierContainer.alpha = 1;
    }

    public removeFakeScatters(): gsap.core.Timeline {
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                EventsManager.trigger(RESET_FAKE_SCATTERS);
            },
        });

        let scatterCounter = 0;

        if (this.fakeScatters) {
            this.fakeScatters.forEach((scatter, scatterIndex) => {
                if (scatter !== null) {
                    tl.add(scatter.getFsTransitionAnimation(), scatterCounter * 0.16);

                    tl.add(this.getLockLowReelAnimation(scatterIndex, undefined, false), `>-=0.25`);

                    scatterCounter++;
                }
            });
        }

        this.fakeScatters = new Array(this.reels.length).fill(null);

        return tl;
    }

    public revealSecondStageReels(isRestore: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onComplete: () => {
                this.onOrientationChange();
            },
        });

        tl.timeScale(0.9);

        const multiplierAnimation = gsap.to(this.fsMultiplierContainer, {
            pixi: { scale: 1.06 },
            ease: `power2.in`,
            x: -18,
            y: 5,
            duration: 0.33,
        });

        tl.add(multiplierAnimation, 0);

        const containerScaleAnimation = gsap.to(this, {
            pixi: { scale: 0.94 },
            ease: `power2.in`,
            x: 20,
            y: 102,
            duration: 0.33,
        });

        tl.add(containerScaleAnimation, 0);

        const backgroundScaleAnimation = gsap.to(this.background, {
            pixi: { scale: 0.98 },
            ease: `power2.in`,
            y: -150,
            duration: 0.33,
        });

        tl.add(backgroundScaleAnimation, 0);

        this.topBottomBlockers // tl.timeScale(0.1);
            .forEach((blocker) => {
                const doorRevealAnimation = blocker.getSpineTween(`door_reveal`);
                doorRevealAnimation.timeScale(1.2);
                tl.add(doorRevealAnimation, 0);
            });

        tl.add(gsap.to(this.reelsContainerMask, { y: -116, height: 654, duration: 0.1 }), 0.23);

        const tlEndLabel = `tlEndLabel`;
        tl.addLabel(tlEndLabel, `>`);

        tl.timeScale(0.85);

        if (isRestore) {
            tl.seek(tl.labels[tlEndLabel], false);
        }

        return tl;
    }

    public unlockSecondStageReels(isRestore: boolean = false): gsap.core.Timeline {
        if (this.alreadyUnlocked) {
            return gsap.timeline();
        }

        this.alreadyUnlocked = true;

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                this.reelsContainer.addChild(this.overground);
                this.fsLeftContainer.switchGraphics(false);
                playSound({ name: ApocalypseSoundNames.UNLOCK_FS_REELS });
            },
        });

        const unlockTl = gsap.timeline();
        unlockTl.timeScale(2.2);

        this.topBottomBlockers.forEach((blocker, index) => {
            const unlockAnimation = blocker.getSpineTween(`unlock`);
            unlockAnimation.timeScale(1.2);

            unlockTl.add(unlockAnimation, index * 0.2);
        });
        tl.add(unlockTl, `doorsUnlockLabel+=0.2`);

        const showOvergroundAnimation = gsap.to(this.overground, {
            alpha: 1,
            duration: 0.8,
        });
        tl.add(showOvergroundAnimation, `>-=0.8`);

        const tlEndLabel = `tlEndLabel`;
        tl.addLabel(tlEndLabel, `>`);

        if (isRestore) {
            tl.seek(tl.labels[tlEndLabel], false);
        }

        return tl;
    }

    private restoreReelsContainer(): void {
        this.reelsContainerMask.y = this.maskDefaultYPos;
        this.reelsContainerMask.height = this.maskDefaultHeight;

        this.background.y = -46;
        this.background.scale.y = 0.7;
    }

    protected override get maskDefaultHeight(): number {
        return 457;
    }

    protected override get maskDefaultYPos(): number {
        return -16;
    }

    public getLockLowReelAnimation(
        reelIndex: number,
        onReelSpinComplete: Function | undefined = undefined,
        isRestore: boolean = false,
        force: boolean = false
    ): gsap.core.Timeline {
        if (this.reels[reelIndex].isLocked && !isRestore && !force) {
            onReelSpinComplete && onReelSpinComplete();
            return gsap.timeline();
        }
        this.reels[reelIndex].isLocked = true;

        const lowReelLocker = new FsLowLocker();

        const tl = gsap.timeline({
            onStart: () => {
                lowReelLocker.x = this.reels[reelIndex].x + symbolConfig.symbolWidth / 2;
                lowReelLocker.y = this.reels[reelIndex].y + symbolConfig.symbolHeight / 2;
                this.middleBlockersContainer.addChild(lowReelLocker);

                if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    gsap.delayedCall(symbolLockDelay, () =>
                        playSound({
                            name: this.lowLockerSoundNames[this.lowLockerCurrentSoundIndex],
                        })
                    );
                } else {
                    playSound({ name: this.lowLockerSoundNames[this.lowLockerCurrentSoundIndex] });
                }

                this.lowLockerCurrentSoundIndex++;
                if (this.lowLockerCurrentSoundIndex > this.lowLockerSoundNames.length - 1) {
                    this.lowLockerCurrentSoundIndex = 0;
                }
            },
            onComplete: () => {
                onReelSpinComplete && onReelSpinComplete();
            },
        });

        tl.add(lowReelLocker.getAnimation(isRestore));

        return tl;
    }

    public getLockHighReelAnimation(
        reelIndex: number,
        isRestore: boolean = false
    ): gsap.core.Timeline {
        const reel = this.reels[reelIndex] as ApocalypseFsReel;

        if (reel.isPersistent) {
            return gsap.timeline();
        }

        reel.isPersistent = true;

        const locker = new FsHighLocker(reelIndex);
        locker.symbol = reel.getAllVisibleStopSymbols()[0] as ApocalypseSymbol;
        const xOffset = locker.isTop ? 0 : 1;
        locker.x = this.reels[reelIndex].x + symbolConfig.symbolWidth / 2 + xOffset;
        this.persistentLockers.push(locker);

        if (isRestore) {
            this.addChild(locker);
            return gsap.timeline();
        }

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });

        tl.add(
            locker.getSpineTween(`reveal`, false, () => {
                this.addChild(locker);

                gsap.delayedCall(0.2, () => {
                    shake(this, 4);
                    playSound({ name: ApocalypseSoundNames.SHAKE_SCREEN });
                });
            }),
            0
        );

        return tl;
    }

    public getLockerByReelId(reelId: number): FsHighLocker | undefined {
        return this.persistentLockers.find((locker) => locker.reelId === reelId);
    }

    public hideAllM5FeatureSymbols(): gsap.core.Timeline {
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });

        const alreadyRemovedM5ReelsIds: number[] = [];

        this.persistentLockers.forEach((locker) => {
            if (locker.symbol.name === ApocalypseHighSymbolNames.M5) {
                const currentReel = this.reels[locker.reelId] as ApocalypseFsReel;
                currentReel.isPersistent = false;
                currentReel.isLocked = false;

                tl.add(
                    locker!.getSpineTween(
                        `dissapear`,
                        false,
                        () => {
                            playSoundOnce({
                                name: ApocalypseSoundNames.SYMBOL_DISSAPEAR,
                                playOnceMinDuration: 0.2,
                            });
                        },
                        null,
                        () => {
                            this.persistentLockers = this.persistentLockers.filter(
                                (l) => l !== locker
                            );
                            locker.destroy();
                        }
                    ),
                    0
                );

                const softlyHideLocker = gsap.to(locker, {
                    alpha: 0,
                    duration: 0.3,
                });

                tl.add(softlyHideLocker, `>-0.35`);

                const hidePersistentGlitchSpine = new AbstractSpine(
                    ApocalypseSpineNames.PERSISTENT_GLITCH
                );
                hidePersistentGlitchSpine.x = locker.x;
                hidePersistentGlitchSpine.y = locker.isTop ? -55 : 474.5;
                this.addChild(hidePersistentGlitchSpine);
                this.addChild(locker);

                const hidePersistentAnimation = hidePersistentGlitchSpine.getSpineTween(
                    locker.symbol.name.toLowerCase()
                );

                tl.add(hidePersistentAnimation, `>-=${hidePersistentAnimation.totalDuration()}`);

                const switchSymbolAnimation = this.reels[locker.reelId].switchHighSymbolToBlank(
                    locker.symbol,
                    hidePersistentAnimation.totalDuration()
                );

                tl.add(switchSymbolAnimation, `<`);

                const hidePersistentSpineAnimation = gsap.to(hidePersistentGlitchSpine, {
                    alpha: 0,
                    duration: 0.12,
                    onComplete: () => {
                        hidePersistentGlitchSpine.destroy();
                    },
                });

                tl.add(hidePersistentSpineAnimation, `>-0.12`);

                alreadyRemovedM5ReelsIds.push(locker.reelId);
            }
        });

        const restOfM5Timeline = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });

        this.reels.forEach((reel, reelIndex) => {
            if (alreadyRemovedM5ReelsIds.includes(reelIndex)) {
                return;
            }

            const m5Symbol = reel.getSymbolAtIndex(1) as ApocalypseSymbol;

            if (m5Symbol.name !== ApocalypseHighSymbolNames.M5) {
                return;
            }

            const hidePersistentGlitchSpine = new AbstractSpine(
                ApocalypseSpineNames.PERSISTENT_GLITCH
            );
            hidePersistentGlitchSpine.x = m5Symbol.x + symbolConfig.symbolWidth / 2;
            hidePersistentGlitchSpine.y = m5Symbol.y + symbolConfig.symbolHeight / 2;

            const hidePersistentAnimation = hidePersistentGlitchSpine.getSpineTween(
                m5Symbol.name.toLowerCase()
            );

            restOfM5Timeline.add(hidePersistentAnimation, 0);

            const switchSymbolAnimation = reel.switchHighSymbolToBlank(
                m5Symbol,
                hidePersistentAnimation.totalDuration()
            );

            restOfM5Timeline.add(switchSymbolAnimation, `<`);

            const hidePersistentSpineAnimation = gsap.to(hidePersistentGlitchSpine, {
                alpha: 0,
                duration: 0.12,
                onComplete: () => {
                    hidePersistentGlitchSpine.destroy();
                },
            });

            restOfM5Timeline.add(hidePersistentSpineAnimation, `>-0.12`);

            m5Symbol.parent.addChild(hidePersistentGlitchSpine);
        });

        tl.add(restOfM5Timeline, 0.2);

        return tl;
    }

    public movePersistentReels(): gsap.core.Timeline {
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });

        const hidePersistentReelsTl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });

        const movePersistentReelsTl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                playSound({ name: ApocalypseSoundNames.MOVE_SYMBOLS });
            },
            onComplete: () => {
                stopSound({ name: ApocalypseSoundNames.MOVE_SYMBOLS });
            },
        });

        this.persistentLockers.forEach((locker) => {
            const currentReel = this.reels[locker.reelId] as ApocalypseFsReel;
            currentReel.isPersistent = false;
            currentReel.isLocked = false;

            if (
                locker.reelId === 5 ||
                locker.reelId === 35 ||
                (isAllTrue(Apocalypse.model.data.fullReels) &&
                    locker.symbol.name === ApocalypseHighSymbolNames.M5)
            ) {
                hidePersistentReelsTl.add(
                    locker!.getSpineTween(
                        `dissapear`,
                        false,
                        () => {
                            playSoundOnce({
                                name: ApocalypseSoundNames.SYMBOL_DISSAPEAR,
                                playOnceMinDuration: 0.2,
                            });
                        },
                        null,
                        () => {
                            this.persistentLockers = this.persistentLockers.filter(
                                (l) => l !== locker
                            );
                            locker.destroy();
                        }
                    ),
                    0
                );

                const softlyHideLocker = gsap.to(locker, {
                    alpha: 0,
                    duration: 0.3,
                });

                hidePersistentReelsTl.add(softlyHideLocker, `>-0.35`);

                const hidePersistentGlitchSpine = new AbstractSpine(
                    ApocalypseSpineNames.PERSISTENT_GLITCH
                );
                hidePersistentGlitchSpine.x = locker.x;
                hidePersistentGlitchSpine.y = locker.isTop ? -55 : 474.5;
                this.addChild(hidePersistentGlitchSpine);
                this.addChild(locker);

                const hidePersistentAnimation = hidePersistentGlitchSpine.getSpineTween(
                    locker.symbol.name.toLowerCase()
                );

                hidePersistentReelsTl.add(
                    hidePersistentAnimation,
                    `>-=${hidePersistentAnimation.totalDuration()}`
                );

                const switchSymbolAnimation = this.reels[locker.reelId].switchHighSymbolToBlank(
                    locker.symbol,
                    hidePersistentAnimation.totalDuration()
                );

                hidePersistentReelsTl.add(switchSymbolAnimation, `<`);

                const hidePersistentSpineAnimation = gsap.to(hidePersistentGlitchSpine, {
                    alpha: 0,
                    duration: 0.12,
                    onComplete: () => {
                        hidePersistentGlitchSpine.destroy();
                    },
                });

                hidePersistentReelsTl.add(hidePersistentSpineAnimation, `>-0.12`);
            } else {
                locker.reset();

                const nextReel = this.reels[locker.reelId + 1] as ApocalypseFsReel;

                const moveLockerAnimation = gsap.to(locker, {
                    x: nextReel.x + symbolConfig.symbolWidth / 2,
                    duration: 1.35,
                    ease: `none`,
                    onStart: () => {
                        const newSymbol = getSymbolFromPool(
                            ApocalypseSpecialSymbolNames.BLANK
                        ) as ApocalypseBlankSymbol;
                        newSymbol.hasSpecialTexture = true;
                        currentReel.updateCurrentSymbol(newSymbol, false);

                        const globalSymbolPosition = locker.symbol.getGlobalPosition();
                        const localSymbolPosition =
                            this.reelsContainer.toLocal(globalSymbolPosition);

                        locker.symbol.position.x = localSymbolPosition.x;
                        locker.symbol.position.y = localSymbolPosition.y;
                        locker.enableMask();
                        this.reelsContainer.addChild(locker.symbol);
                    },
                    onUpdate: () => {
                        locker.symbol.position.x = locker.x - symbolConfig.symbolWidth / 2;
                    },
                    onComplete: () => {
                        locker.reelId = nextReel.reelId;
                        nextReel.isPersistent = true;
                        nextReel.isLocked = true;

                        locker.disableMask();
                        nextReel.updateCurrentSymbol(locker.symbol, true);
                    },
                });

                movePersistentReelsTl.add(moveLockerAnimation, 0);

                const rotateLockerWheelsAnimation = locker.getWheelsAnimation();
                movePersistentReelsTl.add(rotateLockerWheelsAnimation, 0);

                /////////////////
                const nextReelSymbol = nextReel.getSymbolAtIndex(1) as ApocalypseSymbol;

                const nextLockers = this.persistentLockers.filter(
                    (locker) => locker.reelId === nextReel.reelId
                );
                let isNextSymbolPersistent = nextLockers.length > 0;

                if (nextReelSymbol.name in ApocalypseHighSymbolNames && !isNextSymbolPersistent) {
                    const hidePersistentGlitchSpine = new AbstractSpine(
                        ApocalypseSpineNames.PERSISTENT_GLITCH
                    );
                    hidePersistentGlitchSpine.x = nextReelSymbol.x + symbolConfig.symbolWidth / 2;
                    hidePersistentGlitchSpine.y = nextReelSymbol.y + symbolConfig.symbolHeight / 2;

                    const hidePersistentAnimation = hidePersistentGlitchSpine.getSpineTween(
                        nextReelSymbol.name.toLowerCase()
                    );

                    hidePersistentReelsTl.add(hidePersistentAnimation, 0.2);

                    const switchSymbolAnimation = nextReel.switchHighSymbolToBlank(
                        nextReelSymbol,
                        hidePersistentAnimation.totalDuration()
                    );

                    hidePersistentReelsTl.add(switchSymbolAnimation, `<`);

                    const hidePersistentSpineAnimation = gsap.to(hidePersistentGlitchSpine, {
                        alpha: 0,
                        duration: 0.12,
                        onComplete: () => {
                            hidePersistentGlitchSpine.destroy();
                        },
                    });

                    hidePersistentReelsTl.add(hidePersistentSpineAnimation, `>-0.12`);

                    nextReelSymbol.parent.addChild(hidePersistentGlitchSpine);
                }

                /////////////////
            }
        });

        tl.add(hidePersistentReelsTl);
        tl.add(movePersistentReelsTl);

        if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            tl.timeScale(2);
        }

        return tl;
    }

    public onOrientationChange() {
        const screenProps: ScreenProperties = getScreenProperties();
        const resolution = screenProps.bounds.height / screenProps.bounds.width;
        // console.log(`sandru resolution`, resolution);

        if (screenProps.isPortrait) {
            this.scale.set(0.992);
            this.x = 4;
            this.y = 102;

            this.fsLeftContainer.scale.set(1);
            this.fsLeftContainer.x = 116;
            this.fsLeftContainer.y = -175;

            this.background.x = -51;
            this.background.y = -150;
            this.background.scale.set(0.98);

            this.fsMultiplierContainer.x = -202;
            this.fsMultiplierContainer.y = -240;

            if (resolution > 1.32 && resolution < 1.4) {
                this.fsLeftContainer.scale.set(0.7);
                this.fsLeftContainer.x = 91;
                this.fsLeftContainer.y = -152;

                this.fsMultiplierContainer.scale.set(0.96);
                this.fsMultiplierContainer.x = -120;
                this.fsMultiplierContainer.y = -220;
            }
            if (resolution >= 1.4 && resolution < 1.44) {
                this.fsLeftContainer.scale.set(0.8);
                this.fsLeftContainer.x = 94;
                this.fsLeftContainer.y = -168;

                this.fsMultiplierContainer.scale.set(1);
                this.fsMultiplierContainer.x = -152;
                this.fsMultiplierContainer.y = -234;
            }
            // this.fsMultiplierContainer.scale.set(1.06);
            this.addChildAt(this.fsMultiplierContainer, 0);
        } else {
            this.scale.set(0.94);
            this.x = 20;
            this.y = 102;

            this.fsLeftContainer.scale.set(1);
            this.fsLeftContainer.x = -22;
            this.fsLeftContainer.y = 0;

            this.background.x = -51;
            this.background.y = -150;
            this.background.scale.set(0.98);

            this.fsMultiplierContainer.x = -18;
            this.fsMultiplierContainer.y = -5;
            this.fsMultiplierContainer.scale.set(1.06);
            this.addChild(this.fsMultiplierContainer);

            if (resolution > 0.69 && resolution <= 0.73) {
                this.fsLeftContainer.x = -18;

                // this.fsMultiplierContainer.x = -18;
            }

            if (resolution > 0.73 && resolution < 0.76) {
                this.fsLeftContainer.x = -18;

                this.fsMultiplierContainer.scale.set(1);
                this.fsMultiplierContainer.x = 0;
                // this.fsMultiplierContainer.y = -220;
            }
        }
    }

    public override reset(): void {
        super.reset();

        this.alreadyUnlocked = false;

        this.topBottomBlockers.forEach((blocker) => {
            blocker.getSpineTween(`door_revert`);
        });

        this.restoreReelsContainer();

        this.middleBlockersContainer.removeChildren();

        this.fsMultiplierContainer.reset();
        this.fsLeftContainer.reset();

        if (this.persistentLockers.length > 0) {
            this.persistentLockers.forEach((locker) => {
                locker.destroy();
            });
            this.persistentLockers = [];
        }

        this.reelsContainer.removeChild(this.overground);
        this.overground.alpha = 0;

        this.scale.set(1);
        this.position.set(0, reelsConfig.reelsContainerY);

        this.fsLeftContainer.switchGraphics(true);
    }
}
